import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ReactNode, useState } from 'react';

import AppliedFiltersChips from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online.tread_first';

export enum MappingStatusFilterValue {
  MAPPED = 'mapped',
  UNMAPPED = 'unmapped',
}

// AppliedFiltersChips resolves this key through dispatch.dispatch_v2.filters.status
const STATUS_FILTER_KEY = 'status';

interface MappingTabToolbarProps {
  actions?: ReactNode;
  mappedCount: number;
  onSearchQueryChange: (searchQuery: string) => void;
  onStatusFilterChange: (statuses: string[]) => void;
  searchQuery: string;
  statusFilter: string[];
  unmappedCount: number;
}

export default function MappingTabToolbar({
  actions,
  mappedCount,
  onSearchQueryChange,
  onStatusFilterChange,
  searchQuery,
  statusFilter,
  unmappedCount,
}: MappingTabToolbarProps) {
  const theme = useTheme();
  const [isStatusFilterSelected, setIsStatusFilterSelected] = useState(false);

  const hasFiltersApplied = statusFilter.length > 0;
  const clearStatusFilter = () => onStatusFilterChange([]);
  const statusOptions = [
    {
      label: `${t(`${I18N_PREFIX}.unmapped`)} (${unmappedCount})`,
      value: MappingStatusFilterValue.UNMAPPED,
    },
    {
      label: `${t(`${I18N_PREFIX}.mapped`)} (${mappedCount})`,
      value: MappingStatusFilterValue.MAPPED,
    },
  ];

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row', gap: 1 }}>
        <TextField
          data-test-id="quick-books-mapping-search"
          onChange={(event) => onSearchQueryChange(event.target.value)}
          placeholder={t(`${I18N_PREFIX}.search_placeholder`)}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          value={searchQuery}
        />

        <Box
          data-test-id="quick-books-mapping-status-filter"
          sx={{ alignItems: 'center', display: 'flex' }}
        >
          <Menu
            sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
            onOpenStateChanged={(isOpen) => {
              if (!isOpen) {
                setIsStatusFilterSelected(false);
              }
            }}
            menuTrigger={
              <SmallButton
                size="small"
                startIcon={<FilterList />}
                sx={{
                  [`&.${buttonClasses.root}`]: {
                    backgroundColor: theme.semantic.surfaceRaised,
                    color: theme.palette.text.primary,
                    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    ...(hasFiltersApplied
                      ? {
                          borderBottomLeftRadius: theme.brandV2.borderRadius,
                          borderBottomRightRadius: 0,
                          borderTopLeftRadius: theme.brandV2.borderRadius,
                          borderTopRightRadius: 0,
                        }
                      : { borderRadius: theme.brandV2.borderRadius }),
                  },
                }}
              >
                {t('common.filters')}
              </SmallButton>
            }
          >
            {isStatusFilterSelected ? (
              <FilterMenuItem
                onSelectedOptionsChange={onStatusFilterChange}
                options={statusOptions}
                selectAllOptionLabel={t('common.all')}
                selectedOptions={statusFilter}
              />
            ) : (
              <MenuItem onClick={() => setIsStatusFilterSelected(true)}>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                    {t('dispatch.dispatch_v2.filters.status')}
                  </Typography>

                  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                    {hasFiltersApplied && (
                      <Check
                        sx={{
                          color: theme.brandV2.colors.treadOrange,
                          fontSize: '16px',
                        }}
                      />
                    )}

                    <ChevronRight sx={{ fontSize: '16px' }} />
                  </Box>
                </Box>
              </MenuItem>
            )}
          </Menu>

          <AppliedFiltersChips
            appliedFilters={{ [STATUS_FILTER_KEY]: statusFilter }}
            collectedData={{
              [STATUS_FILTER_KEY]: [
                {
                  label: t(`${I18N_PREFIX}.unmapped`),
                  value: MappingStatusFilterValue.UNMAPPED,
                },
                {
                  label: t(`${I18N_PREFIX}.mapped`),
                  value: MappingStatusFilterValue.MAPPED,
                },
              ],
            }}
            onRemoveAllFilters={clearStatusFilter}
            onRemoveFilter={clearStatusFilter}
          />
        </Box>
      </Box>

      {actions}
    </Box>
  );
}
